import { Typography } from '@mui/material';
import { useSearchkit } from '@searchkit/client';
import React, { RefObject, useMemo, useState } from 'react';
import { search } from 'sr-types';
import { SECURITY_ACTIONS } from '../../../common/auth/Security';
import type { SecuredActionType } from '../../../common/components/ActionType';
import Confirm from '../../../common/components/Confirm';
import FilterDisplay from '../../../common/components/FilterDisplay';
import ListMenu from '../../../common/components/ListMenu';
import SaveFilterModal from '../../../common/components/SaveFilterModal';
import { ENTITY_CONSTANTS } from '../../../common/constants/entityConstants';
import { useBrowserOrAccountLocale } from '../../../common/hooks/useBrowserOrAccountLocale';
import { useSavedFilters } from '../../../common/hooks/useSavedFilters';
import I18n from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';
import { V } from '../../../common/Layout';
import { useStyling } from '../../../common/Theme';
import { Filter, SavedFilterItem } from '../../../common/types/savedFilters';
import { successToastMessage } from '../../../common/utils/commonUtils';
import { getDisplayFilters, getGroupedFilters, toLabel } from '../../../production/helpers/savedFiltersUtils';

type SavedFiltersListProps = {
    preferenceEntityKey: string;
    immutableFilters?: Filter[];
    facets?: search.SkFacetSet[];
    isSavedFilterChangeRef?: RefObject<boolean>;
};

const EmptyState = () => (
    <V fill>
        <Typography sx={{ m: 1 }}>
            <I18n token="filter.savedFilter.list.empty" />
        </Typography>
    </V>
);

const FilterItem = ({
    item,
    onApply,
    onDelete,
    onEdit,
    facets
}: {
    item: SavedFilterItem;
    onApply: (preferenceId: string) => void;
    onDelete?: (name: string, id: string) => void;
    onEdit?: (item: SavedFilterItem) => void;
    facets?: search.SkFacetSet[];
}) => {
    const { theme } = useStyling();
    const locale = useBrowserOrAccountLocale();

    const securedActions: SecuredActionType[] = useMemo(
        () => [
            ...(onEdit
                ? [
                      {
                          securityAction: SECURITY_ACTIONS.EDIT,
                          entityName: ENTITY_CONSTANTS.preference,
                          entitySecurity: item.security,
                          label: <I18n token="actions.edit" />,
                          icon: Icons.EditOutlined,
                          onClick: (e) => {
                              e?.stopPropagation();
                              onEdit(item);
                          },
                          dataTestId: 'edit-filter'
                      }
                  ]
                : []),
            ...(onDelete
                ? [
                      {
                          securityAction: SECURITY_ACTIONS.DELETE,
                          entityName: ENTITY_CONSTANTS.preference,
                          entitySecurity: item.security,
                          label: <I18n token="actions.delete" />,
                          icon: Icons.Delete,
                          onClick: (e) => {
                              e?.stopPropagation();
                              onDelete(item.name, item.id);
                          },
                          dataTestId: 'delete-filter'
                      }
                  ]
                : [])
        ],
        [item, onDelete, onEdit]
    );

    return (
        <ListMenu
            item={item}
            renderItemLabel={(it) => (
                <Typography sx={{ width: '100%', fontWeight: theme.typography.fontWeightLight }}>{it.name}</Typography>
            )}
            onRowClick={() => item.id && onApply(item.id)}
            securedActions={securedActions}
            renderItemTooltip={() => (
                <V sx={{ p: 1, minWidth: 260 }}>
                    <FilterDisplay
                        groupedFilters={getGroupedFilters(getDisplayFilters(item.filters), locale)}
                        toLabel={(id) => toLabel(id, facets || []) || id}
                        description={item?.description}
                    />
                </V>
            )}
        />
    );
};

export default ({
    preferenceEntityKey,
    immutableFilters = [],
    facets,
    isSavedFilterChangeRef
}: SavedFiltersListProps) => {
    const api = useSearchkit();
    const {
        state: { savedFilters, savingFilter, deletingFilter },
        actions: { applySavedFilter, deleteFilter, saveFilter }
    } = useSavedFilters(preferenceEntityKey, api, { immutableFilters });

    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [filterToDelete, setFilterToDelete] = useState<{ name: string; id: string } | null>(null);
    const [filterToEdit, setFilterToEdit] = useState<SavedFilterItem | null>(null);

    const handleDeleteClick = (name: string, id: string) => {
        setFilterToDelete({ name, id });
        setDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (filterToDelete && deleteFilter) {
            await deleteFilter(filterToDelete.id);
            setDeleteConfirmationOpen(false);
            setFilterToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
        setFilterToDelete(null);
    };

    if (!savedFilters?.length) {
        return <EmptyState />;
    }

    const handleCloseEdit = () => {
        setFilterToEdit(null);
    };

    const handleEditSave = async (data: {
        name: string;
        description: string;
        shared: boolean;
        pinAsTab?: boolean;
        data: Filter[];
    }) => {
        if (!filterToEdit?.id) return;
        await saveFilter(
            {
                name: data.name,
                description: data.description,
                shared: data.shared,
                pinAsTab: data.pinAsTab
            },
            filterToEdit.filters,
            filterToEdit.accountId,
            filterToEdit.id
        );
        handleCloseEdit();
    };

    const handleApply = (preferenceId: string) => {
        applySavedFilter?.(preferenceId);
        if (isSavedFilterChangeRef) isSavedFilterChangeRef.current = true;
        successToastMessage('filter.savedFilter.apply.success');
    };
    return (
        <V sx={{ overflowX: 'hidden', flexGrow: 2 }}>
            <V>
                {savedFilters.map((item) => (
                    <FilterItem
                        key={item.id}
                        item={item}
                        onApply={handleApply}
                        onDelete={handleDeleteClick}
                        onEdit={setFilterToEdit}
                        facets={facets}
                    />
                ))}
            </V>
            <SaveFilterModal
                open={!!filterToEdit}
                onClose={handleCloseEdit}
                onSave={handleEditSave}
                appliedFilters={filterToEdit?.filters || []}
                isLoading={savingFilter}
                facets={facets}
                isEdit
                initialData={{
                    name: filterToEdit?.name || '',
                    description: filterToEdit?.description || '',
                    shared: filterToEdit?.shared !== undefined ? filterToEdit.shared : false,
                    pinAsTab: Boolean(filterToEdit?.pinAsTab)
                }}
            />

            <Confirm
                open={deleteConfirmationOpen}
                title={<I18n token="delete.confirm.title" />}
                okLabelI18n="delete.confirm.title"
                body={
                    <Typography>
                        <I18n token="delete.confirm.message" />
                    </Typography>
                }
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isLoading={deletingFilter}
            />
        </V>
    );
};
